import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  Box,
  TextField,
  Typography,
  Checkbox,
  CircularProgress,
  List,
  ListItemIcon,
  ListItemText,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { addDriveToCollection, removeDriveFromCollection } from '../store/collection.actions';
import { fetchDrivesByCollectionForModal } from '../../drive/store/drive.actions';
import { Close } from '@mui/icons-material';
import {
  StyledCloseButton,
  ModalHeader,
  UserListContainer,
  UserListItem,
  StyledSubmitBox,
  StyledButton,
} from '../../../utils/common/styles/commonStyles';
import { fetchCollectionDrives } from '../store/collection.actions';

const ManageCollectionModal = ({
  open,
  onClose,
  onUpdate,
  collectionId,
  collectionName,
  companyId,
}) => {
  // collectionId: the collection to manage drives for
  // collectionName: display name of the collection
  // companyId: used to fetch available drives for the company
  const dispatch = useDispatch();
  const [allDrives, setAllDrives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [checkedDrives, setCheckedDrives] = useState(new Set());
  const [initialInCollection, setInitialInCollection] = useState(new Set());
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // when the modal opens with valid identifiers, load all drives and mark those in collection
    if (open && collectionId && companyId) {
      loadDrives();
    }
  }, [open, collectionId, companyId, dispatch]);

  const loadDrives = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const resp = await dispatch(fetchCollectionDrives(companyId));
      const allDrivesList = resp?.data?.data ?? [];

      // Fetch drives already in the collection (without affecting main drive state)
      const collResp = await dispatch(fetchDrivesByCollectionForModal(collectionId));
      const inCollection = collResp?.data?.data ?? [];
      const inIds = new Set(inCollection.map((d) => d.id));

      setAllDrives(allDrivesList);
      setInitialInCollection(inIds);
      setCheckedDrives(new Set(inIds)); // pre-check those in collection
      setShowSubmitButton(false);
    } catch (error) {
      setErrorMessage('Failed to load drives. Please try again.');
      setAllDrives([]);
      setInitialInCollection(new Set());
      setCheckedDrives(new Set());
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortKey(e.target.value);
  };

  // reset from previous open
  const resetState = () => {
    setSearchTerm('');
    setCheckedDrives(new Set());
    setInitialInCollection(new Set());
    setShowSubmitButton(false);
    setErrorMessage('');
  };

  const handleCheckboxChange = (driveId) => {
    setCheckedDrives((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(driveId)) {
        newSet.delete(driveId);
      } else {
        newSet.add(driveId);
      }
      // Show submit button if there are changes
      const hasChanges =
        ![...newSet].every((id) => initialInCollection.has(id)) ||
        ![...initialInCollection].every((id) => newSet.has(id));
      setShowSubmitButton(hasChanges);
      return newSet;
    });
  };

  // apply search and sort
  const filteredDrives = (() => {
    let list = allDrives.filter((drive) =>
      drive.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortKey === 'name') {
      list.sort((a, b) => a.name?.localeCompare(b.name));
    } else if (sortKey === 'recent') {
      // sort by createdAt descending; fallback to id to stabilize
      list.sort((a, b) => {
        const da = new Date(a.createdAt || 0).getTime();
        const db = new Date(b.createdAt || 0).getTime();
        return db - da || a.id - b.id;
      });
    }

    return list;
  })();

  const handleSubmit = async () => {
    const toAdd = [...checkedDrives].filter((id) => !initialInCollection.has(id));
    const toRemove = [...initialInCollection].filter((id) => !checkedDrives.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) return;

    setSubmitting(true);
    setErrorMessage('');

    try {
      if (toAdd.length > 0) {
        await dispatch(addDriveToCollection(collectionId, toAdd));
      }
      if (toRemove.length > 0) {
        await dispatch(removeDriveFromCollection(collectionId, toRemove));
      }

      const addMsg = toAdd.length > 0 ? `Added ${toAdd.length} drive(s)` : '';
      const removeMsg = toRemove.length > 0 ? `Removed ${toRemove.length} drive(s)` : '';
      const msg = [addMsg, removeMsg].filter(Boolean).join(', ');
      // notify parent so it can refresh and show toast
      onUpdate &&
        onUpdate({ message: `Successfully updated collection: ${msg}`, severity: 'success' });

      // Update initial state to reflect changes
      setInitialInCollection(new Set(checkedDrives));
      setShowSubmitButton(false);

      // close modal immediately; toast persists
      handleClose();
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || 'Failed to update collection. Please try again.'
      );
      // error remains visible in modal until closed
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Main screen with drives list
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            position: 'relative',
            minHeight: '500px',
            maxHeight: '80vh',
          },
        }}
      >
        <ModalHeader>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
            Manage Collection: {collectionName}
          </Typography>
          <StyledCloseButton size="small" onClick={handleClose} sx={{ color: 'common.white' }}>
            <Close fontSize="small" />
          </StyledCloseButton>
        </ModalHeader>
        <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Search drives..."
            value={searchTerm}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            disabled={loading}
            sx={{ backgroundColor: 'background.paper', borderRadius: 1 }}
          />
          <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="sort-label">Sort/Filter</InputLabel>
            <Select
              labelId="sort-label"
              value={sortKey}
              onChange={handleSortChange}
              label="Sort/Filter"
            >
              <MenuItem value="name">Name (A→Z)</MenuItem>
              <MenuItem value="recent">Recently Added</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {/* Dialog Content */}
        <DialogContent
          sx={{
            p: 2,
            minHeight: '300px',
            display: 'flex',
            flexDirection: 'column',
            pb: 12,
          }}
        >
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress size={40} />
            </Box>
          ) : allDrives.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography color="textSecondary">No drives available for this Company</Typography>
            </Box>
          ) : filteredDrives.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography color="textSecondary">No drives match your search</Typography>
            </Box>
          ) : (
            <UserListContainer>
              <List sx={{ padding: 1 }}>
                {filteredDrives.map((drive) => (
                  <UserListItem key={drive.id} disablePadding>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={checkedDrives.has(drive.id)}
                        onChange={() => handleCheckboxChange(drive.id)}
                        tabIndex={-1}
                        disableRipple
                        disabled={submitting}
                        sx={{
                          '&.Mui-checked': {
                            color: 'green',
                          },
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={drive.name}
                      secondary={drive.description}
                      sx={{
                        '& .MuiListItemText-secondary': {
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  </UserListItem>
                ))}
              </List>
            </UserListContainer>
          )}
        </DialogContent>

        {/* Sticky Submit Button - Always visible */}
        <StyledSubmitBox showButton>
          <StyledButton
            variant="contained"
            onClick={handleSubmit}
            disabled={!showSubmitButton || submitting}
          >
            {submitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : 'Update Collection'}
          </StyledButton>
        </StyledSubmitBox>
      </Dialog>
    </>
  );
};

ManageCollectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func,
  collectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  collectionName: PropTypes.string,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ManageCollectionModal;
